import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import Grid from "@mui/material/Grid";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftEditor from "components/SoftEditor";
import SoftSelect from "components/SoftSelect";
import FormField from "../FormField";
import CategoriesApi from "api/categories";
import { useTranslation } from "react-i18next";
import { Switch } from "@mui/material";
import SupplierApi from "api/supplier";

const ProductInfo = forwardRef(({ onChange, initialValues }, ref) => {
  const { t } = useTranslation();

  const [productName, setProductName] = useState(initialValues?.productName || "");
  const [referenceCode, setReferenceCode] = useState(initialValues?.referenceCode || "");
  const [description, setDescription] = useState(initialValues?.description || "");
  const [category, setCategory] = useState(initialValues?.category || "");
  const [mainCategory, setMainCategory] = useState(initialValues?.mainCategory || "");
  const [brandCategory, setBrandCategory] = useState(initialValues?.brandCategory || "");
  const [supplier, setSupplier] = useState(initialValues?.supplier || []);
  const [status, setStatus] = useState(initialValues?.status ?? true);
  const [categories, setCategories] = useState([]);
  const [mainCategories, setMainCategories] = useState([]);
  const [brandCategories, setBrandCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [isTaxed, setIsTaxed] = useState(initialValues?.isTaxed ?? false);
  const [errors, setErrors] = useState({});

  useImperativeHandle(ref, () => ({
    validateForm,
  }));

  // Initial fetch (main categories + suppliers)
  useEffect(() => {
    fetchMainCategories();
    fetchSuppliers();
  }, []);

  // Quand mainCategory change → recharger brandCategories
  useEffect(() => {
    if (mainCategory) {
      fetchBrandCategories(mainCategory.value);
      setBrandCategory(""); // reset brand category
      setCategories([]); // reset product categories
    }
  }, [mainCategory]);

  // Quand brandCategory change → charger les product categories
  useEffect(() => {
    if (mainCategory && brandCategory) {
      fetchProductCategories(mainCategory.value, brandCategory.value);
    }
  }, [mainCategory, brandCategory]);

  // Notify parent of changes
  useEffect(() => {
    onChange({
      productName,
      referenceCode,
      description,
      category,
      mainCategory,
      brandCategory,
      supplier,
      status,
      isTaxed,
    });
    // eslint-disable-next-line
  }, [
    productName,
    referenceCode,
    description,
    category,
    mainCategory,
    brandCategory,
    supplier,
    status,
    isTaxed,
  ]);

  // API Calls
  const fetchProductCategories = (mainCategoryID, brandCategoryID) => {
    const token = localStorage.getItem("token");
    if (!token) return;

    CategoriesApi.GetProductCategories(token, mainCategoryID, brandCategoryID)
      .then((response) => {
        setCategories(response.data.results || []);
      })
      .catch((error) => {
        console.error(error.response?.data?.msg || error.message);
      });
  };

  const fetchMainCategories = () => {
    const token = localStorage.getItem("token");
    if (!token) return;

    CategoriesApi.GetMainCategories(token)
      .then((response) => {
        setMainCategories(response.data.results || []);
      })
      .catch((error) => {
        console.error("Failed to fetch main categories:", error);
      });
  };

  const fetchBrandCategories = (mainCategoryID) => {
    const token = localStorage.getItem("token");
    if (!token) return;

    CategoriesApi.GetBrandCategories(token, mainCategoryID)
      .then((response) => {
        setBrandCategories(response.data.results || []);
      })
      .catch((error) => {
        console.error("Failed to fetch brand categories:", error);
      });
  };

  const fetchSuppliers = async () => {
    const token = localStorage.getItem("token");
    try {
      const supplierData = await SupplierApi.GetSuppliersList(token);
      setSuppliers(supplierData.data.results || []);
    } catch (error) {
      console.error("Failed to fetch suppliers:", error);
    }
  };

  // Handle inputs
  const handleInputChange = (field, value) => {
    setErrors((prevErrors) => ({ ...prevErrors, [field]: "" }));
    switch (field) {
      case "productName":
        setProductName(value);
        break;
      case "referenceCode":
        setReferenceCode(value);
        break;
      case "description":
        setDescription(value);
        break;
      case "category":
        setCategory(value);
        break;
      case "mainCategory":
        setMainCategory(value);
        setBrandCategory(""); // reset brand category
        setCategories([]); // reset product categories
        break;
      case "brandCategory":
        setBrandCategory(value);
        break;
      case "supplier":
        setSupplier(value);
        break;
      case "status":
        setStatus(value);
        break;
      default:
        break;
    }
  };

  // Validation
  const validateForm = () => {
    const newErrors = {};
    if (!productName)
      newErrors.productName = `${t("Le nom de produit est requis")}`;
    if (!referenceCode)
      newErrors.referenceCode = `${t("Le code de référence est requis")}`;
    if (!category) newErrors.category = `${t("La catégorie est requise")}`;
    if (!mainCategory)
      newErrors.mainCategory = `${t("La catégorie principale est requise")}`;
    if (!brandCategory)
      newErrors.brandCategory = `${t("La catégorie de marque est requise")}`;
    if (!supplier || supplier.length === 0)
      newErrors.supplier = `${t("Le fournisseur est requis")}`;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  return (
    <SoftBox>
      <SoftTypography variant="h5">
        {t("Informations sur le produit")}
      </SoftTypography>
      <SoftBox mt={3}>
        <Grid container spacing={3}>
          {/* Nom produit + code */}
          <Grid item xs={12} sm={6}>
            <FormField
              type="text"
              label={t("Nom de produit")}
              placeholder={t("Nom de produit")}
              value={productName}
              error={!!errors.productName}
              helperText={errors.productName}
              onChange={(e) => handleInputChange("productName", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormField
              type="text"
              label={t("Code de référence")}
              placeholder="eg. 6191122554421"
              value={referenceCode}
              error={!!errors.referenceCode}
              helperText={errors.referenceCode}
              onChange={(e) =>
                handleInputChange("referenceCode", e.target.value)
              }
            />
          </Grid>
        </Grid>
      </SoftBox>

      <SoftBox mt={2}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            {/* Catégorie principale */}
            <SoftBox mb={3}>
              <SoftTypography
                component="label"
                variant="caption"
                fontWeight="bold"
              >
                {t("Catégorie principale")}
              </SoftTypography>
              <SoftSelect
                value={mainCategory}
                error={!!errors.mainCategory}
                helperText={errors.mainCategory}
                onChange={(value) => handleInputChange("mainCategory", value)}
                options={mainCategories.map((cat) => ({
                  value: cat.id.toString(),
                  label: t(cat.main_category_name),
                }))}
              />
            </SoftBox>

            {/* Catégorie */}
            <SoftBox>
              <SoftTypography
                component="label"
                variant="caption"
                fontWeight="bold"
              >
                {t("Catégorie")}
              </SoftTypography>
              <SoftSelect
                value={category}
                error={!!errors.category}
                helperText={errors.category}
                onChange={(value) => handleInputChange("category", value)}
                options={categories.map((cat) => ({
                  value: cat.id.toString(),
                  label: t(cat.category_name),
                }))}
              />
            </SoftBox>
          </Grid>

          <Grid item xs={12} sm={6}>
            {/* Catégorie de marque */}
            <SoftBox mb={3}>
              <SoftTypography
                component="label"
                variant="caption"
                fontWeight="bold"
              >
                {t("Catégorie de marque")}
              </SoftTypography>
              <SoftSelect
                value={brandCategory}
                error={!!errors.brandCategory}
                helperText={errors.brandCategory}
                onChange={(value) => handleInputChange("brandCategory", value)}
                options={brandCategories.map((brand) => ({
                  value: brand.id.toString(),
                  label: t(brand.brand_category_name),
                }))}
              />
            </SoftBox>

            {/* Fournisseur */}
            <SoftBox>
              <SoftTypography variant="caption" fontWeight="bold">
                {t("Fournisseur")}
              </SoftTypography>
              <SoftSelect
                isMulti
                value={supplier}
                error={!!errors.supplier}
                helperText={errors.supplier}
                onChange={(selectedOptions) =>
                  handleInputChange("supplier", selectedOptions)
                }
                options={suppliers.map((supplier) => ({
                  value: supplier.id,
                  label: supplier.name,
                }))}
              />
            </SoftBox>
          </Grid>

          {/* Description */}
          <Grid item xs={12} sm={6}>
            <SoftTypography
              component="label"
              variant="caption"
              fontWeight="bold"
            >
              {t("Description")}{" "}
              <SoftTypography
                variant="caption"
                fontWeight="regular"
                color="text"
              >
                ({t("optionnel")})
              </SoftTypography>
            </SoftTypography>
            <SoftEditor value={description} onChange={setDescription} />
          </Grid>

          {/* Statuts */}
          <Grid
            item
            xs={12}
            sm={6}
            display="flex"
            justifyContent="space-around"
            mt={5}
          >
            <SoftBox display="flex" alignItems="center" mb={2}>
              <Switch
                checked={status}
                onChange={(e) => setStatus(e.target.checked)}
              />
              <SoftTypography variant="caption" fontWeight="bold" ml={1}>
                {status ? t("Actif") : t("Inactif")}
              </SoftTypography>
            </SoftBox>
            <SoftBox display="flex" alignItems="center" mb={2}>
              <Switch
                checked={isTaxed}
                onChange={(e) => setIsTaxed(e.target.checked)}
              />
              <SoftTypography variant="caption" fontWeight="bold" ml={1}>
                {isTaxed ? t("Taxé") : t("Non Taxé")}
              </SoftTypography>
            </SoftBox>
          </Grid>
        </Grid>
      </SoftBox>
    </SoftBox>
  );
});

export default ProductInfo;
